<template>
  <!-- Float owns the panel's placement and portalling: `bottom-end` opens it below the trigger and
       right-aligned to it, `flip` moves it above the trigger when it does not fit below, `shift`
       clamps it into the viewport, and `portal` lifts it out of the statements table, which clips its
       own overflow. `floatingAs="template"` keeps the panel itself the positioned element: Bootstrap's
       .dropdown-menu is absolutely positioned, so wrapped in a Float div it would leave the flow and
       Floating UI would measure a 0x0 box. `zIndex` clears the Bootstrap modal (1055), so the menu
       also works inside the edit-order modal. -->
  <Float
    placement="bottom-end"
    :offset="4"
    flip
    shift
    portal
    floatingAs="template"
    :zIndex="9999"
    :show="isOpen"
  >
    <!-- Float takes over the reference element's template ref, so the trigger sits in a wrapper that
         hugs it and keeps `triggerRef` free for the outside-click handler. -->
    <span class="d-inline-flex">
      <!-- The trigger doubles as the row's privacy readout: a lock replaces the options icon while the
           statement is private, so the state costs no extra row height (a badge under the region select
           pushed the row to two lines). The word "Private" still appears in the panel, the review modal
           and the order-detail modal. -->
      <button
        ref="triggerRef"
        type="button"
        class="btn btn-link btn-sm p-0 statement-options-trigger"
        :class="{ 'statement-options-trigger--private': keepPrivate }"
        :aria-label="
          $t(
            keepPrivate
              ? 'go_cancel.statement_options_button_private_aria'
              : 'go_cancel.statement_options_button_aria',
            { index: statementIndex + 1 }
          )
        "
        :title="
          keepPrivate ? $t('go_cancel.statement_private_tooltip') : undefined
        "
        :aria-expanded="isOpen"
        :aria-controls="panelId"
        @click="toggle"
      >
        <FontAwesomeIcon :icon="keepPrivate ? 'lock' : 'sliders-h'" />
      </button>
    </span>

    <div :id="panelId" class="dropdown-menu show statement-options-panel">
      <div class="form-check form-switch mb-1">
        <input
          :id="switchId"
          v-model="keepPrivate"
          class="form-check-input"
          type="checkbox"
          role="switch"
        />
        <label class="form-check-label" :for="switchId">
          {{ $t("go_cancel.form.keep_private.label") }}
        </label>
      </div>
      <div class="form-text mb-0">
        {{ $t("go_cancel.form.keep_private.hint") }}
      </div>
    </div>
  </Float>
</template>

<script setup lang="ts">
import { Float } from "@headlessui-float/vue";
import { onClickOutside, useEventListener } from "@vueuse/core";
import { computed, ref, useTemplateRef } from "vue";

const props = defineProps<{
  /** Row index, used for unique element ids and the trigger's aria-label. */
  statementIndex: number;
}>();

/** Whether this statement's cancellation statement is kept private. */
const keepPrivate = defineModel<boolean>({ required: true });

const isOpen = ref(false);
const triggerRef = useTemplateRef<HTMLElement>("triggerRef");

const panelId = computed(
  () => `statement-options-panel-${props.statementIndex}`
);
const switchId = computed(() => `keep-private-${props.statementIndex}`);

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const close = () => {
  isOpen.value = false;
};

// Float renders the panel from its own clone of the vnode, so a template ref on it never resolves —
// the panel is matched by class instead. Only one panel is ever open, so this cannot ignore a sibling.
onClickOutside(triggerRef, close, { ignore: [".statement-options-panel"] });

useEventListener("keydown", (event: KeyboardEvent) => {
  if (event.key === "Escape" && isOpen.value) {
    close();
  }
});
</script>

<style scoped lang="scss">
.statement-options-trigger {
  color: var(--phoenix-secondary-color);

  &:hover,
  &--private {
    color: var(--phoenix-primary);
  }
}

:global(.statement-options-panel) {
  display: block;
  width: 320px;
  max-width: calc(100vw - 16px);
  padding: 0.75rem;
}
</style>
